import {concatPath} from "#graph/helpers/path"
import type {NodeParameters} from "#graph/nodes/base/types"
import {isClass} from "@cm/utils"
import type {z} from "zod"

export const mapGraphParameters = <GraphNode, MapResult = GraphNode>(
    parameters: NodeParameters,
    // add this parameter to prevent circular dependencies between a BaseGraphNode and its helpers
    isGraphNode: (value: unknown) => boolean,
    nodeMapper?: (node: GraphNode, path: string) => MapResult,
): NodeParameters => {
    const mapParameter = (parameter: unknown, path: string): unknown => {
        if (isGraphNode(parameter)) return nodeMapper ? nodeMapper(parameter as GraphNode, path) : parameter
        if (typeof parameter !== "object" || !parameter) return parameter
        if (Array.isArray(parameter)) return parameter.map((entry, index) => mapParameter(entry, concatPath(path, index)))
        if (isClass(parameter)) return parameter
        const keys = Object.keys(parameter)
        const result: NodeParameters = {}
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i]
            result[key] = mapParameter((parameter as NodeParameters)[key], concatPath(path, key))
        }
        return result
    }

    return mapParameter(parameters, "") as NodeParameters
}

export const traverseGraphParameters = <GraphNode>(
    parameters: NodeParameters,
    isGraphNode: (value: unknown) => boolean,
    callback?: (node: GraphNode, path: string) => GraphNode | void,
): NodeParameters => {
    return mapGraphParameters<GraphNode, GraphNode | void>(
        parameters,
        isGraphNode,
        callback
            ? (node: GraphNode, path: string) => {
                  const retValue = callback(node, path)
                  if (isGraphNode(retValue)) {
                      return retValue
                  } else {
                      return node
                  }
              }
            : undefined,
    )
}

export const ensureValidParameters = <GraphNode extends {paramsSchema: z.ZodType<NodeParameters>; nodeClass: string}>(
    graph: GraphNode,
    parameters: NodeParameters,
): NodeParameters => {
    const schema = graph.paramsSchema
    const result = schema.safeParse(parameters)
    if (!result.success) {
        throw new Error(`Invalid parameters for node ${graph.nodeClass}: ${JSON.stringify(result.error.issues)}`, {
            cause: {parameters, issues: result.error.issues, schema},
        })
    } else {
        return result.data
    }
}
